/* eslint-disable react/prop-types */
import React, { useContext, useState } from 'react';
import { useStore } from '@nanostores/react';
import { Grid, GridItem } from '@bwoty-web/ui-kit';
import { StoreContext } from '../../../store';
import Wrapper from './Wrapper';

const rules = {
  1: [12],
  2: [12, 12],
  3: [6, 6, 12],
  4: [6, 6, 6, 6],
  5: [6, 6, 4, 4, 4],
  6: [4, 4, 4, 4, 4, 4],
};

const assignColumns = (batch) => {
  const spans = rules[batch.length];
  return batch.map((item, index) => {
    return React.cloneElement(item, { columnSpan: spans[index] });
  });
};

const createBatches = (items, maxBatchSizes) => {
  const batches = [];
  let batch = [];
  let currBatchSizeIndex = 0;
  const uniqueBatchTypesFound = [];

  const resetState = () => {
    batch = [];

    const batchIsFullyIterated =
      currBatchSizeIndex === maxBatchSizes.length - 1;
    if (batchIsFullyIterated) {
      currBatchSizeIndex = 0;
    } else {
      currBatchSizeIndex += 1;
    }
  };

  const getItemType = (item) => {
    if (item.type.displayName) {
      return item.type.displayName;
    }

    if (item.type.name) {
      return item.type.name;
    }

    return 'unknown';
  };

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    const itemType = getItemType(item);
    const nextItem = items[i + 1];
    const nextItemType = nextItem && getItemType(nextItem);

    batch.push(item);

    const batchIsComplete = batch.length === maxBatchSizes[currBatchSizeIndex];
    const isLastItem = i === items.length - 1;
    const nextItemIsDifferentType = nextItemType !== itemType;

    // If we have a complete batch, or we're at the last item, or the next item is of a different type
    // We want to end the current batch, save it and reset state so we can start a new batch
    if (batchIsComplete || isLastItem || nextItemIsDifferentType) {
      batches.push(assignColumns(batch));
      resetState();
    }

    if (!uniqueBatchTypesFound.includes(itemType)) {
      uniqueBatchTypesFound.push(itemType);
    }
  }

  return { batches, uniqueBatchTypesFound };
};

const reorganizeBatches = (batches, numOfBatchTypes) => {
  const result = [];
  const numOfGroups = Math.ceil(batches.length / numOfBatchTypes);

  for (let groupIndex = 0; groupIndex < numOfGroups; groupIndex += 1) {
    for (
      let batchTypeIndex = 0;
      batchTypeIndex < numOfBatchTypes;
      batchTypeIndex += 1
    ) {
      const batchIndex = batchTypeIndex * numOfGroups + groupIndex;
      if (batchIndex < batches.length) {
        result.push(batches[batchIndex]);
      }
    }
  }

  return result;
};

const processItems = (items, maxBatchSizes, mode = 'No mix') => {
  const { batches, uniqueBatchTypesFound } = createBatches(
    items,
    maxBatchSizes,
  );

  if (mode === 'Every other') {
    return reorganizeBatches(batches, uniqueBatchTypesFound.length);
  }

  return batches;
};

const CrazyList = ({
  elements = [],
  moreLabel,
  lessLabel,
  visibleElements: initialVisibleElements,
  topItemId,
  mixMode: initialMixMode,
  batchSizes: initialBatchSizes,
  getFilteredElementsData = (elems) => ({
    filteredElements: elems,
    contentTypeCount: {},
  }),
}) => {
  const maxBatchSizes = JSON.parse(initialBatchSizes) || [4, 3, 2, 6, 1];
  const [maxBatchSizeEndIndex, setMaxBatchSizeEndIndex] = useState(1);

  const mixMode = initialMixMode || 'No mix';

  const { initStore } = useContext(StoreContext);
  const store = initStore(topItemId, {
    visibleItems: maxBatchSizes[0],
    mixMode,
  });
  const { visibleItems } = useStore(store);

  const { filteredElements } = getFilteredElementsData(elements);
  const sortedElements = processItems(filteredElements, maxBatchSizes, mixMode);

  const batchedElements = sortedElements.slice(0, maxBatchSizeEndIndex);
  const elementsToRender = batchedElements.flat();

  const handleOnExpand = () => {
    const newIndex = maxBatchSizeEndIndex + 1;
    const newStepSize = sortedElements[newIndex - 1].length;

    setMaxBatchSizeEndIndex(newIndex);
    store.setKey('visibleItems', visibleItems + newStepSize);
  };

  return (
    <Wrapper
      moreLabel={moreLabel}
      lessLabel={lessLabel}
      numOfRenderedElems={elementsToRender.length}
      numOfTotalElems={elements.length}
      numOfFilteredElems={filteredElements.length}
      initialNumberOfVisibleElements={initialVisibleElements}
      onCollapse={() => setMaxBatchSizeEndIndex(1)}
      onExpand={handleOnExpand}
    >
      <Grid columns={12}>
        {elementsToRender.map((element, i) => (
          <GridItem
            columnStart={0}
            columnEnd={12}
            columnSpan={element.props.columnSpan}
            key={i}
          >
            {element}
          </GridItem>
        ))}
      </Grid>
    </Wrapper>
  );
};
CrazyList.app = 'idun';
CrazyList.displayName = 'CrazyList';

export default CrazyList;
